/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/** Displays the shared supply icons and their intended visual semantics. */

import type { LucideIcon } from 'lucide-react';

/* Import components */
import CodeBlock from '@/components/code/CodeBlock';
import {
    ElectricityIcon,
    ElectricityNightIcon,
    GasIcon,
    HeatIcon,
    PhotovoltaicsIcon,
    ReportIcon,
    WaterIcon,
} from '@/components/icon/SupplyIcon';
import LayoutSectionHeader from '@/components/tabs/layouts/LayoutSectionHeader';

interface SupplyIconExample {
    description: string;
    icon: LucideIcon;
    name: string;
}

const SUPPLY_ICON_EXAMPLES: readonly SupplyIconExample[] = [
    {
        description: 'Represents solar energy generation and photovoltaic values.',
        icon: PhotovoltaicsIcon,
        name: 'PhotovoltaicsIcon',
    },
    {
        description: 'Represents electricity consumption, base load, and grid supply values.',
        icon: ElectricityIcon,
        name: 'ElectricityIcon',
    },
    {
        description: 'Represents electricity monitoring values that apply to the night period.',
        icon: ElectricityNightIcon,
        name: 'ElectricityNightIcon',
    },
    {
        description: 'Represents heating energy and thermal monitoring values.',
        icon: HeatIcon,
        name: 'HeatIcon',
    },
    {
        description: 'Represents water consumption and water-loss monitoring values.',
        icon: WaterIcon,
        name: 'WaterIcon',
    },
    {
        description: 'Represents gas consumption and related monitoring values.',
        icon: GasIcon,
        name: 'GasIcon',
    },
    {
        description: 'Represents reports and aggregated monitoring results.',
        icon: ReportIcon,
        name: 'ReportIcon',
    },
];

function createIconCode(name: string): string {
    return `import { ${name} } from '@/components/icon/SupplyIcon';

<${name}
    className="h-10 w-10"
    aria-hidden="true"
/>`;
}

interface IconsLayoutsTabProps {
    theme: 'light' | 'dark';
}

function IconsLayoutsTab({ theme }: IconsLayoutsTabProps) {
    return (
        <div className="layouts-tab">
            <LayoutSectionHeader sectionKey="icons" />

            {SUPPLY_ICON_EXAMPLES.map(({ description, icon: Icon, name }) => (
                <section key={name} className="layouts-section">
                    <h3 className="text-xl font-semibold">{name}</h3>
                    <p className="text-base-content/70">{description}</p>

                    <h4 className="tracking-wide">Code example</h4>
                    <div className="layouts-code-tabs">
                        <div className="tabs tabs-lift">
                            <button type="button" className="tab tab-active">
                                JSX
                            </button>
                        </div>
                        <CodeBlock
                            className="layouts-code--tabbed"
                            code={createIconCode(name)}
                            roundedTop={false}
                            theme={theme}
                        />
                    </div>

                    <h4 className="tracking-wide">Rendered preview</h4>
                    <section className="dashboard-panel layouts-demo-panel items-start justify-start p-6">
                        <Icon className="h-12 w-12" aria-hidden="true" />
                    </section>
                </section>
            ))}
        </div>
    );
}

export default IconsLayoutsTab;
